import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Grid,
  Paper,
  TextField,
  Typography,
  Tabs,
  Tab,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Link,
} from '@mui/material';
import {
  getCategory,
  getCategoryAttributes,
  getInheritedVariableCosts,
  updateCategory,
  moveCategory,
  setCategoryAttributes,
  getCategoryProducts,
} from '../../../app/api';
import { useTranslation } from '../../../app/LanguageContext';
import { useExtensions } from '../../../app/ExtensionsContext';
import useUnsavedChangesGuard from '../../core/public/useUnsavedChangesGuard';
import MediaGallery from '../../medias/public/MediaGallery';
import CategoryParentSelect from '../private/CategoryParentSelect';
import CategoryAttributesEditor from '../private/CategoryAttributesEditor';
import CategoryVariableCostsSection from '../private/CategoryVariableCostsSection';
import { normalizeCategory, slugifyName } from '../public/categoryUtils';
import {
  buildCategoryDefaultsMetadataPatch,
  getCategoryVariableCostDefaults,
  normalizeVariableCostRows,
  PRICE_SIMULATOR_EXTENSION_KEY,
  splitInheritedCategoryVariableCosts,
} from '../../../extensions/price-simulator-3d/priceSimulatorMetadata';

const VARIABLE_COST_AUTOSAVE_DEBOUNCE_MS = 700;

function variableCostSnapshot(rows) {
  return JSON.stringify(
    normalizeVariableCostRows(rows, { keepEmptyDraft: false })
      .map((row) => ({ id: row.id, name: row.name, cost_unit: row.cost_unit }))
      .sort((a, b) => a.id.localeCompare(b.id)),
  );
}

function formSnapshot(form) {
  return JSON.stringify({
    name: (form.name ?? '').trim(),
    slug: (form.slug ?? '').trim(),
    description: (form.description ?? '').trim(),
    parent_id: form.parent_id || '',
    status: form.status || 'ACTIVE',
  });
}

function attributesSnapshot(list) {
  return JSON.stringify(
    [...(list || [])]
      .map((d) => ({
        id: d.attribute_specification_id,
        req: Boolean(d.is_required),
      }))
      .sort((a, b) => a.id.localeCompare(b.id)),
  );
}

const CategoryFormPage = () => {
  const { id } = useParams();
  const { translate } = useTranslation();
  const { isEnabled } = useExtensions();
  const simulatorEnabled = isEnabled(PRICE_SIMULATOR_EXTENSION_KEY);

  const [tab, setTab] = useState('dados');
  const [loading, setLoading] = useState(true);
  const [hydrated, setHydrated] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categoryId, setCategoryId] = useState(null);
  const [loadedParentId, setLoadedParentId] = useState('');
  const [form, setForm] = useState({
    name: '',
    slug: '',
    description: '',
    parent_id: '',
    status: 'ACTIVE',
  });
  const [attributesDirect, setAttributesDirect] = useState([]);
  const [baselineForm, setBaselineForm] = useState('');
  const [baselineAttributes, setBaselineAttributes] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [products, setProducts] = useState([]);
  const [productsTotal, setProductsTotal] = useState(0);
  const [productsPage, setProductsPage] = useState(0);
  const [productsRows, setProductsRows] = useState(25);
  const [productsLoading, setProductsLoading] = useState(false);
  const [variableCostDirect, setVariableCostDirect] = useState([]);
  const [inheritedVariableCosts, setInheritedVariableCosts] = useState([]);

  const variableCostsSaveTimer = useRef(null);
  const variableCostBaselineSnapshotRef = useRef('');
  const variableCostDirectLatestRef = useRef([]);

  const isDirty = useMemo(() => {
    if (!hydrated) return false;
    return formSnapshot(form) !== baselineForm || attributesSnapshot(attributesDirect) !== baselineAttributes;
  }, [hydrated, form, baselineForm, attributesDirect, baselineAttributes]);

  useUnsavedChangesGuard(isDirty && !loading, translate('categories.unsaved_leave_confirm'));

  const applyBaseline = useCallback((nextForm, nextAttributes, parentId) => {
    setBaselineForm(formSnapshot(nextForm));
    setBaselineAttributes(attributesSnapshot(nextAttributes));
    setLoadedParentId(parentId || '');
    setHydrated(true);
  }, []);

  const loadCategory = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setHydrated(false);
    try {
      const requests = [
        getCategory(id),
        getCategoryAttributes(id),
      ];
      if (simulatorEnabled) {
        requests.push(getInheritedVariableCosts(id));
      }
      const results = await Promise.all(requests);
      const catRes = results[0];
      const attrRes = results[1];
      const inheritedCostsRes = simulatorEnabled ? results[2] : null;
      const cat = normalizeCategory(catRes.data);
      if (!cat) throw new Error(translate('categories.not_found'));
      const parentId = cat.parent_id || '';
      const nextForm = {
        name: cat.name,
        slug: cat.slug,
        description: cat.description,
        parent_id: parentId,
        status: cat.status,
      };
      const nextAttributes = attrRes.data?.direct ?? [];
      setCategoryId(cat.id);
      setForm(nextForm);
      setAttributesDirect(nextAttributes);
      setSlugTouched(false);
      if (simulatorEnabled) {
        const effectiveDefaults = inheritedCostsRes?.data?.defaults ?? [];
        const { inherited } = splitInheritedCategoryVariableCosts(effectiveDefaults, cat.id);
        const directCosts = getCategoryVariableCostDefaults(cat.metadata || {});
        setInheritedVariableCosts(inherited);
        setVariableCostDirect(directCosts);
        variableCostDirectLatestRef.current = directCosts;
        variableCostBaselineSnapshotRef.current = variableCostSnapshot(directCosts);
      } else {
        setInheritedVariableCosts([]);
        setVariableCostDirect([]);
      }
      applyBaseline(nextForm, nextAttributes, parentId);
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [id, translate, applyBaseline, simulatorEnabled]);

  const loadProducts = useCallback(async () => {
    if (!categoryId) return;
    setProductsLoading(true);
    try {
      const res = await getCategoryProducts(categoryId, {
        page: productsPage + 1,
        limit: productsRows,
      });
      setProducts(res.data?.products ?? []);
      setProductsTotal(res.data?.total ?? 0);
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setProductsLoading(false);
    }
  }, [categoryId, productsPage, productsRows]);

  useEffect(() => {
    void loadCategory();
  }, [loadCategory]);

  useEffect(() => {
    if (tab === 'produtos' && categoryId) void loadProducts();
  }, [tab, categoryId, loadProducts]);

  const persistVariableCostDefaults = useCallback(
    async (rows) => {
      if (!categoryId) return;
      try {
        await updateCategory(categoryId, {
          metadata: buildCategoryDefaultsMetadataPatch(rows),
        });
        variableCostBaselineSnapshotRef.current = variableCostSnapshot(rows);
        setSnackbar({
          open: true,
          message: translate('categories.variable_cost_recalc_saved'),
          severity: 'warning',
          autoHideDuration: 6000,
        });
      } catch (err) {
        setSnackbar({ open: true, message: err.message, severity: 'error' });
      }
    },
    [categoryId, translate],
  );

  const handleVariableCostDirectChange = useCallback(
    (rows) => {
      variableCostDirectLatestRef.current = rows;
      setVariableCostDirect(rows);
      if (!categoryId) return;
      if (variableCostsSaveTimer.current) clearTimeout(variableCostsSaveTimer.current);
      variableCostsSaveTimer.current = setTimeout(() => {
        const latest = variableCostDirectLatestRef.current;
        if (variableCostSnapshot(latest) === variableCostBaselineSnapshotRef.current) return;
        void persistVariableCostDefaults(latest);
      }, VARIABLE_COST_AUTOSAVE_DEBOUNCE_MS);
    },
    [categoryId, persistVariableCostDefaults],
  );

  useEffect(() => {
    return () => {
      if (variableCostsSaveTimer.current) clearTimeout(variableCostsSaveTimer.current);
    };
  }, []);

  const handleSave = async () => {
    if (!categoryId || !isDirty) return;
    setSaving(true);
    try {
      const trimmedName = form.name.trim();
      const payload = {
        name: trimmedName,
        description: form.description.trim(),
        status: form.status,
      };
      if (slugTouched || trimmedName) {
        payload.slug = form.slug.trim() || slugifyName(trimmedName);
      }
      await updateCategory(categoryId, payload);
      const nextParentId = form.parent_id || '';
      if (nextParentId !== loadedParentId) {
        await moveCategory(categoryId, { parent_id: nextParentId });
      }
      await setCategoryAttributes(categoryId, {
        attributes: attributesDirect.map((d) => ({
          attribute_specification_id: d.attribute_specification_id,
          is_required: Boolean(d.is_required),
        })),
      });
      const savedForm = {
        ...form,
        name: trimmedName,
        slug: payload.slug ?? form.slug,
        description: form.description.trim(),
        parent_id: nextParentId,
      };
      setForm(savedForm);
      applyBaseline(savedForm, attributesDirect, nextParentId);
      setSnackbar({ open: true, message: translate('common.saved'), severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="contained"
          disabled={saving || !categoryId || !isDirty}
          onClick={() => void handleSave()}
        >
          {saving ? translate('common.saving') : translate('common.save')}
        </Button>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab value="dados" label={translate('categories.tab_data')} />
        {categoryId && <Tab value="atributos" label={translate('categories.tab_attributes')} />}
        {categoryId && <Tab value="produtos" label={translate('categories.tab_products')} />}
      </Tabs>

      {tab === 'dados' && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 2, mb: 2 }}>
              <TextField
                fullWidth
                label={translate('categories.name')}
                value={form.name}
                onChange={(e) => {
                  const name = e.target.value;
                  setForm((p) => ({
                    ...p,
                    name,
                    slug: slugTouched ? p.slug : slugifyName(name),
                  }));
                }}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label={translate('categories.slug')}
                value={form.slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setForm((p) => ({ ...p, slug: slugifyName(e.target.value) }));
                }}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                multiline
                minRows={3}
                label={translate('categories.description')}
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              />
            </Paper>
            {categoryId && (
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>{translate('categories.images')}</Typography>
                <MediaGallery metadataKey="category_image" metadataValue={categoryId} accept="image/*" />
              </Paper>
            )}
            {categoryId && simulatorEnabled && (
              <CategoryVariableCostsSection
                inheritedRows={inheritedVariableCosts}
                directRows={variableCostDirect}
                onDirectChange={handleVariableCostDirectChange}
                disabled={saving}
              />
            )}
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2 }}>
              <CategoryParentSelect
                value={form.parent_id}
                excludeId={categoryId || ''}
                onChange={(parentId) => setForm((p) => ({ ...p, parent_id: parentId }))}
              />
              <FormControl component="fieldset" sx={{ mt: 2, width: '100%' }}>
                <Typography variant="subtitle2" component="legend" sx={{ mb: 0.5 }}>
                  {translate('categories.status')}
                </Typography>
                <RadioGroup
                  value={form.status}
                  onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
                >
                  <FormControlLabel
                    value="ACTIVE"
                    control={<Radio size="small" />}
                    label={translate('products.active')}
                  />
                  <FormControlLabel
                    value="INACTIVE"
                    control={<Radio size="small" />}
                    label={translate('products.inactive')}
                  />
                </RadioGroup>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                {translate('categories.order_hint')}{' '}
                <Link component={RouterLink} to="/products/categories">
                  {translate('categories.title')}
                </Link>
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {tab === 'atributos' && categoryId && (
        <Paper sx={{ p: 2 }}>
          <CategoryAttributesEditor
            categoryId={categoryId}
            direct={attributesDirect}
            onDirectChange={setAttributesDirect}
            disabled={saving}
          />
        </Paper>
      )}

      {tab === 'produtos' && categoryId && (
        <Paper sx={{ p: 0 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{translate('products.list_sort_name')}</TableCell>
                  <TableCell>{translate('products.list_sort_code')}</TableCell>
                  <TableCell>{translate('products.list_sort_status')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {productsLoading ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center"><CircularProgress size={24} /></TableCell>
                  </TableRow>
                ) : products.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3}>{translate('categories.products_empty')}</TableCell>
                  </TableRow>
                ) : (
                  products.map((p) => (
                    <TableRow key={p.id} hover>
                      <TableCell>
                        <Link component={RouterLink} to={`/products/${encodeURIComponent(p.code || p.id)}/edit`}>
                          {p.name}
                        </Link>
                      </TableCell>
                      <TableCell>{p.code}</TableCell>
                      <TableCell>{p.status}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={productsTotal}
            page={productsPage}
            onPageChange={(_, p) => setProductsPage(p)}
            rowsPerPage={productsRows}
            onRowsPerPageChange={(e) => {
              setProductsRows(parseInt(e.target.value, 10));
              setProductsPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Paper>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.autoHideDuration ?? 4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default CategoryFormPage;
